import React, { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  LuMapPin, 
  LuMail, 
  LuPhone, 
  LuArrowRight, 
  LuStar,
  LuFacebook, 
  LuTwitter, 
  LuLinkedin, 
  LuInstagram,
  LuSend
} from "react-icons/lu";
import axios from 'axios';

const Contact = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    nom: "",
    email: "",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    
    const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://127.0.0.1:5003';
    const country = localStorage.getItem('yros_country') || 'sn';

    try {
      const { data } = await axios.post(`${API_URL}/api/contact`, {
        country,
        name: form.nom,
        email: form.email,
        message: form.message,
        subject: `Nouveau message de contact — ${form.nom}`,
      });

      if (data?.status !== 'sent') {
        throw new Error(data?.error || 'Envoi échoué');
      }

      setSent(true);
      setForm({
        nom: "",
        email: "",
        message: "",
      });
    } catch (err) {
      console.error('Erreur d\'envoi:', err);
      setError(t('contact.error_generic'));
    } finally {
      setSending(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const contactInfos = [
    {
      icon: <LuMapPin className="w-5 h-5" />,
      title: t('contact.address_title'),
      content: t('contact.address_content'),
      bgColor: "bg-gradient-to-br from-orange-50 to-orange-100",
      iconColor: "text-orange-600",
      accentColor: "border-orange-200"
    },
    {
      icon: <LuMail className="w-5 h-5" />,
      title: t('contact.email_title'),
      content: "contact@yros.io",
      bgColor: "bg-gradient-to-br from-emerald-50 to-emerald-100",
      iconColor: "text-emerald-600",
      accentColor: "border-emerald-200"
    },
    {
      icon: <LuPhone className="w-5 h-5" />,
      title: t('contact.phone_title'),
      content: "+221 33 869 13 00",
      bgColor: "bg-gradient-to-br from-blue-50 to-blue-100",
      iconColor: "text-blue-600",
      accentColor: "border-blue-200"
    }
  ];

  const socialLinks = [
    { icon: <LuFacebook className="w-6 h-6" />, href: "https://www.facebook.com", color: "hover:text-blue-600" },
    { icon: <LuTwitter className="w-6 h-6" />, href: "https://www.twitter.com", color: "hover:text-sky-500" },
    { icon: <LuLinkedin className="w-6 h-6" />, href: "https://www.linkedin.com", color: "hover:text-blue-700" },
    { icon: <LuInstagram className="w-6 h-6" />, href: "https://www.instagram.com", color: "hover:text-pink-600" }
  ];

  return (
    <main className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 py-20 px-4">
      {/* Decorative Background Elements */}
      <div className="fixed inset-0 overflow-hidden -z-10">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-200/20 to-yellow-200/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-orange-200/50 rounded-full px-4 py-2 mb-4 shadow-lg">
            <LuStar className="w-4 h-4 text-orange-500" />
            <span className="text-sm font-medium text-gray-700">{t('contact.badge')}</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('contact.heading')}{" "}
            <span className="bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">
              {t('contact.heading_highlight')}
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Left Column - Contact Info */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-2xl shadow-lg p-8 h-full">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.coordinates_heading')}</h2>
              
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="space-y-6"
              >
                {contactInfos.map((info, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className={`flex items-center gap-4 p-4 rounded-xl ${info.bgColor} border border-${info.accentColor} shadow-sm`}
                  >
                    <div className={`p-3 bg-white/80 rounded-full shadow-sm ${info.iconColor}`}>
                      {info.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{info.title}</h3>
                      <p className="text-gray-700">{info.content}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
              
              <div className="mt-10 pt-6 border-t border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('contact.follow_us')}</h3>
                <div className="flex justify-center gap-6">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`text-gray-600 transition-all hover:-translate-y-1 ${social.color}`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {t('contact.form_heading')}
              </h2>

              {sent ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="bg-gradient-to-r from-green-50 to-emerald-50 p-8 rounded-2xl border border-green-200 text-center space-y-4"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full mx-auto flex items-center justify-center">
                    <LuSend className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">{t('contact.sent_title')}</h4>
                  <p className="text-gray-600">
                    {t('contact.sent_message')}
                  </p>
                  <button
                    onClick={() => {setSent(false)}}
                    className="inline-flex items-center gap-2 text-green-600 font-medium hover:text-green-700 transition-colors mt-4"
                  >
                    {t('contact.send_another')}
                    <LuArrowRight className="w-4 h-4" />
                  </button>
                </motion.div>
              ) : (
                <motion.form 
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="space-y-5" 
                  onSubmit={handleSubmit}
                >
                  {error && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-50 text-red-600 p-4 rounded-xl text-center border border-red-200"
                    >
                      {error}
                    </motion.div>
                  )}
                  
                  <motion.div variants={itemVariants}>
                    <label className="block text-sm font-medium mb-2" htmlFor="nom">
                      {t('contact.label_name')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="nom"
                      name="nom"
                      required
                      value={form.nom}
                      onChange={handleChange}
                      placeholder={t('contact.placeholder_name')}
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200"
                    />
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <label className="block text-sm font-medium mb-2" htmlFor="email">
                      {t('contact.label_email')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder={t('contact.placeholder_email')}
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200"
                    />
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <label className="block text-sm font-medium mb-2" htmlFor="message">
                      {t('contact.label_message')} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      required
                      value={form.message}
                      onChange={handleChange}
                      placeholder={t('contact.placeholder_message')}
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200"
                    />
                  </motion.div>

                  <motion.button
                    variants={itemVariants}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={sending}
                    className={`w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 ${
                      sending ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    {sending ? t('contact.sending') : t('contact.submit')}
                    {!sending && <LuArrowRight className="w-5 h-5" />}
                  </motion.button>

                  <motion.p variants={itemVariants} className="text-sm text-gray-500 text-center mt-4">
                    {t('contact.privacy_note')}
                  </motion.p>
                </motion.form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </main>
  );
};

export default Contact;